import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLiveSettings } from "@/hooks/content";
import { useTranslatedRecord } from "@/hooks/useTranslatedList";

export default function LiveNowSection() {
  const { t } = useTranslation();
  const { record: rawSettings } = useLiveSettings();
  const liveSettings = useTranslatedRecord(rawSettings, [
    "currentProgram",
    "liveTitle",
    "liveDescription",
    "nextProgram",
    "currentPresenter",
  ]);
  const isLive = liveSettings.sourceType !== "offline";
  const isOffline = liveSettings.sourceType === "offline";

  return (
    <section className="bg-background-100 py-12 md:py-16">
      <div className="ltv-container">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500 text-white">
              <i className="ri-broadcast-line text-lg"></i>
            </div>
            <div>
              <h2 className="ltv-section-title mb-0">L+TV LIVE</h2>
            </div>
          </div>
          <Link
            to="/live"
            className="ltv-btn-accent text-sm inline-flex items-center justify-center gap-2 w-full sm:w-auto whitespace-nowrap"
          >
            {t("home.openLive")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Player */}
          <div className="lg:col-span-2">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              {isLive && !isOffline ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20television%20broadcast%20live%20feed%20screen%20with%20L%20TV%20branding%20overlay%2C%20modern%20TV%20studio%20broadcasting%20live%2C%20clean%20production%20quality%20image%2C%20warm%20lighting%2C%20editorial%20style&width=1200&height=675&seq=live-placeholder&orientation=landscape"
                    alt="L+TV Live Stream"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/20"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="ltv-live-badge mb-3 mx-auto inline-flex">
                        <i className="ri-broadcast-fill"></i>
                        LIVE
                      </div>
                      <p className="text-white text-lg font-semibold">{liveSettings.liveTitle}</p>
                      <p className="text-white/70 text-sm mt-1">{liveSettings.currentProgram}</p>
                      <button className="mt-4 w-16 h-16 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                        <i className="ri-play-fill text-white text-2xl"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-primary-900/90">
                  <div className="w-16 h-16 rounded-full bg-primary-700 flex items-center justify-center mb-4">
                    <i className="ri-tv-line text-white text-2xl"></i>
                  </div>
                  <p className="text-white text-lg font-semibold mb-1">{t("home.offline")}</p>
                  <p className="text-white/60 text-sm text-center max-w-md px-4">
                    {liveSettings.liveDescription}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-white/50 text-sm">
                    <i className="ri-time-line"></i>
                    {t("home.next")}: {liveSettings.nextProgram}
                  </div>
                </div>
              )}

              {/* Player Controls */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3">
                <div className="flex items-center gap-3">
                  <button className="text-white/80 hover:text-white transition-colors">
                    <i className="ri-play-fill text-lg"></i>
                  </button>
                  <button className="text-white/80 hover:text-white transition-colors">
                    <i className="ri-volume-up-line text-lg"></i>
                  </button>
                  <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full w-1/3 bg-accent-500 rounded-full"></div>
                  </div>
                  <span className="text-white/60 text-xs">00:00 / 00:00</span>
                  <button className="text-white/80 hover:text-white transition-colors">
                    <i className="ri-fullscreen-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Info Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-background-200 p-5 h-full flex flex-col">
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  {isLive ? (
                    <span className="ltv-live-badge">
                      <i className="ri-broadcast-fill"></i>
                      LIVE
                    </span>
                  ) : (
                    <span className="bg-foreground-200 text-foreground-600 text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      OFFLINE
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-1">
                  {liveSettings.currentProgram}
                </h3>
                <p className="text-sm text-foreground-600">
                  {liveSettings.liveDescription}
                </p>
              </div>

              <div className="border-t border-background-200 pt-4 mb-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-2">
                  {t("home.presenter")}
                </p>
                <p className="text-sm font-medium text-foreground-800">
                  {liveSettings.currentPresenter}
                </p>
              </div>

              <div className="border-t border-background-200 pt-4 mt-auto">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-2">
                  {t("home.upNext")}
                </p>
                <p className="text-sm font-medium text-foreground-800">
                  {liveSettings.nextProgram}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}